import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { LocationType, SocialMediaType } from 'types/common';
import {
  createOrganizationJSONLD,
  getAbsoluteUrl,
  getSiteName,
  hasSocialMedia,
  mapSameAs,
  OrganizationSEO,
} from 'helpers/seo-helpers';

type SEOProps = {
  title: string
  description: string
  ogSlug: string
  ogImageUrl?: string
  jsonLDArray?: string[]
  children?: JSX.Element
};

type SitewideSchema = {
  strapiSitewideSchema: {
    telephone?: string
    organizationName: string
    email?: string
    socialMedia: SocialMediaType
    logo: {
      url: string
    }
    location: LocationType
  }
};

const staticQuery = graphql`
    query {
      strapiSitewideSchema {
        telephone
        organizationName
        email
        socialMedia {
          instagram
          tiktok
          youtube
          facebook
        }
        logo {
          url
        }
        location {
          addressCountry
          addressLocality
          addressRegion
          postalCode
        }
      }
    }
  `;

const SEO = ({
  jsonLDArray, title, description, ogImageUrl, ogSlug, children,
}: SEOProps) => {
  const { strapiSitewideSchema } = useStaticQuery<SitewideSchema>(staticQuery);

  const organizationSEO: OrganizationSEO = {
    logo: strapiSitewideSchema.logo.url,
    name: strapiSitewideSchema.organizationName,
    url: process.env.GATSBY_DOMAIN_URL!,
    ...(hasSocialMedia(strapiSitewideSchema.socialMedia)) && {
      sameAs: mapSameAs(strapiSitewideSchema.socialMedia),
    },
    ...(strapiSitewideSchema.telephone) && { telephone: strapiSitewideSchema.telephone },
    ...(strapiSitewideSchema.email) && { email: strapiSitewideSchema.email },
    ...(strapiSitewideSchema.location) && { address: strapiSitewideSchema.location },
  };

  const defaultOgImage = ogImageUrl ?? strapiSitewideSchema.logo.url;

  return (
    <>
      <html lang="en" />
      <meta property="og:url" content={getAbsoluteUrl(ogSlug)} />
      <meta property="og:site_name" content={getSiteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={defaultOgImage} />
      <script type="application/ld+json">{createOrganizationJSONLD(organizationSEO)}</script>
      <meta name="description" content={description} />
      <title>{title}</title>
      {jsonLDArray && jsonLDArray.length > 0 && jsonLDArray.map((json, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <script key={`${index}jsonLD`} type="application/ld+json">{json}</script>
      ))}
      {children}
    </>
  );
};

export { SEO };
